import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useState, useEffect } from "react";
import api from "./api/api";

const XIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Reusable accordion wrapper
function AccordionSection({ title, children }) {
  const [open, setOpen] = useState(false);

  return (
    <section className="bg-white rounded-md shadow-sm border">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex justify-between items-center px-3 py-2 text-sm font-semibold bg-gray-100 hover:bg-gray-200 rounded-t-md"
      >
        <span>{title}</span>
        <span className="text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="p-3 max-h-[calc(100vh-150px)] overflow-auto">
          {children}
        </div>
      )}
    </section>
  );
}

function Modal({ isOpen, onClose, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-md shadow-md w-[96%] sm:w-[90%] md:max-w-[1100px] max-h-[88vh] overflow-hidden relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <XIcon />
        </button>
        <div className="h-full overflow-y-auto p-3">{children}</div>
      </div>
    </div>
  );
}

function DataTable({ rows, onGrnClick, highlightGrn }) {
  const [filters, setFilters] = useState({});
  const [visibleFilters, setVisibleFilters] = useState({});

  if (!rows || rows.length === 0) {
    return <p className="text-gray-500 text-xs italic">No data found.</p>;
  }

  const headers = Object.keys(rows[0]);
  const filteredRows = rows.filter((row) =>
    headers.every((h) =>
      (row[h] ?? "")
        .toString()
        .toLowerCase()
        .includes((filters[h] ?? "").toLowerCase())
    )
  );

  const toggleFilter = (header) => {
    setVisibleFilters((prev) => ({ ...prev, [header]: !prev[header] }));
  };

  return (
    <div className="overflow-auto max-h-[60vh] border rounded">
      <table className="w-full min-w-[500px] text-xs border-collapse">
        <thead>
          <tr className="bg-gray-200 text-gray-700">
            {headers.map((h) => (
              <th
                key={h}
                className="px-2 py-1 text-left font-medium border-b border-gray-300 sticky top-0 bg-gray-200 z-10"
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <span>{h}</span>
                    <button
                      onClick={() => toggleFilter(h)}
                      className="text-[10px] text-gray-600 hover:text-black"
                      title="Toggle filter"
                    >
                      🔍
                    </button>
                  </div>
                  {visibleFilters[h] && (
                    <input
                      type="text"
                      placeholder="Filter"
                      value={filters[h] || ""}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, [h]: e.target.value }))
                      }
                      className="mt-1 p-0.5 text-[10px] border rounded focus:outline-none focus:ring focus:ring-blue-300"
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
              {headers.map((h) => (
                <td
                  key={h}
                  className="px-2 py-1 border-b border-gray-200 whitespace-nowrap"
                >
                  {highlightGrn && h === "GRNId" ? (
                    <span
                      onClick={() => onGrnClick(row[h])}
                      className="text-blue-600 cursor-pointer hover:underline"
                    >
                      SelectRef
                    </span>
                  ) : (
                    row[h] ?? "-"
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const data = state?.data;

  const [folderFiles, setFolderFiles] = useState([]);
  const [relatedDocs, setRelatedDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupGrnId, setPopupGrnId] = useState(null);
  const [htmlReport, setHtmlReport] = useState("");
  const [viewingAttachments, setViewingAttachments] = useState(false);
  const [viewingRelatedDocs, setViewingRelatedDocs] = useState(false);

  if (!data)
    return <p className="p-3 text-red-500 text-sm">No data passed.</p>;

  const findTable8 = (obj) => {
    if (!obj) return null;
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const got = findTable8(item);
        if (got) return got;
      }
      return null;
    }
    if (typeof obj === "object") {
      if (obj.Table8 && Array.isArray(obj.Table8)) return obj.Table8;
      for (const k of Object.keys(obj)) {
        try {
          const got = findTable8(obj[k]);
          if (got) return got;
        } catch {}
      }
    }
    return null;
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    if (data.InvoicePartDetails?.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(data.InvoicePartDetails),
        "Invoice & Part Details"
      );
    if (data.ProductionStages?.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(data.ProductionStages),
        "Production Stages"
      );
    if (data.MaterialIssues?.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(data.MaterialIssues),
        "Material Issues"
      );
    if (data.Summary?.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(data.Summary),
        "Summary"
      );
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "Report.xlsx");
  };

  const loadFolderFiles = async (folderUrl) => {
    try {
      setLoading(true);
      setFolderFiles([]);
      setViewingRelatedDocs(false);
      const res = await fetch(`/api/get-files?folder=${encodeURIComponent(folderUrl)}`);
      const files = await res.json();
      setFolderFiles(files);
      setViewingAttachments(true);
    } catch (err) {
      console.error("Error loading files:", err);
      setFolderFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHtmlReport = async (grnId) => {
    try {
      setLoading(true);
      setHtmlReport("");
      setViewingAttachments(false);
      setViewingRelatedDocs(false);
      const htmlStr = await api.getHtmlReport(grnId);
      setHtmlReport(htmlStr || "");
    } catch (err) {
      console.error("Error fetching GRN report:", err);
      alert("Failed to load GRN report.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedDocuments = async (grnId) => {
    try {
      setLoading(true);
      setViewingAttachments(false);
      setHtmlReport("");
      setRelatedDocs([]);
      const json = await api.getIndividualData(grnId);
      const table8 = findTable8(json) || [];
      setRelatedDocs(table8);
      setViewingRelatedDocs(true);
    } catch (err) {
      console.error("Error fetching related documents:", err);
      alert("Failed to load related documents.");
      setRelatedDocs([]);
      setViewingRelatedDocs(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGrnClick = (grnId) => {
    setPopupGrnId(grnId);
    setIsPopupOpen(true);
    setHtmlReport("");
    setViewingAttachments(false);
    setViewingRelatedDocs(false);
  };

  const handleBackToMenu = () => {
    navigate("/search");
  };

  return (
    <div className="max-w-7xl mx-auto py-3 space-y-4">
      <div className="flex justify-end mb-2 gap-2">
        <button
          onClick={handleBackToMenu}
          className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md shadow-sm"
        >
          Back
        </button>
        <button
          onClick={exportToExcel}
          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded shadow"
        >
          Export to Excel
        </button>
      </div>

      {/* Accordions */}
      <AccordionSection title="Invoice & Part Details">
        <DataTable rows={data.InvoicePartDetails} />
      </AccordionSection>

      <AccordionSection title="Production Stages">
        <DataTable rows={data.ProductionStages} />
      </AccordionSection>

      <AccordionSection title="Material Issue Reference">
        <DataTable
          rows={data.MaterialIssues}
          onGrnClick={handleGrnClick}
          highlightGrn={true}
        />
      </AccordionSection>

      <AccordionSection title="Summary">
        <DataTable rows={data.Summary} />
      </AccordionSection>

      {/* Popup Modal */}
      <Modal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 mb-2">
            <button
              onClick={() => fetchHtmlReport(popupGrnId)}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded shadow"
            >
              GRN Report
            </button>
            <button
              onClick={() => fetchRelatedDocuments(popupGrnId)}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded shadow"
            >
              Supplier Invoice
            </button>
            {data.AttachmentsDocUrl && (
              <button
                onClick={() => loadFolderFiles(data.AttachmentsDocUrl)}
                className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded shadow"
              >
                View Attachments
              </button>
            )}
          </div>
          {loading && <p className="text-gray-500 text-sm">Loading...</p>}

          {!loading && htmlReport && !viewingAttachments && !viewingRelatedDocs && (
            <div
              className="border p-2 rounded bg-gray-50 max-h-[65vh] overflow-auto text-sm"
              dangerouslySetInnerHTML={{ __html: htmlReport }}
            />
          )}

          {!loading && viewingAttachments && folderFiles.length > 0 && (
            <div className="space-y-1 max-h-[65vh] overflow-auto border p-2 rounded bg-gray-50 text-sm">
              {folderFiles.map((file, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="truncate">{file.name}</span>
                  <button
                    onClick={() => window.open(file.url, "_blank")}
                    className="text-blue-600 hover:underline text-xs"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          )}
          {!loading && viewingAttachments && folderFiles.length === 0 && (
            <p>No attachments found.</p>
          )}

          {!loading && viewingRelatedDocs && relatedDocs.length > 0 && (
            <div className="space-y-1 max-h-[65vh] overflow-auto border p-2 rounded bg-gray-50 text-sm">
              {relatedDocs.map((doc, i) => {
                const ref =
                  doc.RefFilePath ||
                  doc.RefFilepath ||
                  doc.RefFile ||
                  doc.RefPath ||
                  doc.FileUrl ||
                  "";
                const name = doc.DisplayFileName || ref.split("/").pop();
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-2 p-2 border rounded bg-white"
                  >
                    <div className="truncate text-xs">{name}</div>
                    <button
                      onClick={() => window.open(ref, "_blank")}
                      className="px-2 py-0.5 text-xs bg-green-600 text-white rounded"
                    >
                      View
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {!loading && viewingRelatedDocs && relatedDocs.length === 0 && (
            <p>No related documents found.</p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default ResultsPage;
